/**
 * État temps réel d'une salle POP!.
 *
 * - Connexion Socket.IO sur la même origine (`io()` sans URL : en production
 *   nginx proxifie `/socket.io`, en local le proxy Vite fait de même pour
 *   `/api` — aucune URL d'hôte codée en dur).
 * - Authentification de reprise : `{ code, playerId }` (session de jeu, pas de
 *   compte).
 * - L'état complet est REMPLACÉ à chaque `SERVER_EVENT.State`, jamais patché :
 *   le serveur est la seule source de vérité.
 * - À chaque (re)connexion, on redemande l'état (`CLIENT_EVENT.Sync`) : sans
 *   cela, un joueur coupé 5 s resterait bloqué sur un état périmé sans le
 *   savoir.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  CLIENT_EVENT,
  SERVER_EVENT,
  finishedSchema,
  rejectedSchema,
  roundResolvedSchema,
  roundSchema,
  roomStateSchema,
  type Finished,
  type Rejected,
  type RoomState,
  type Round,
  type RoundResolved,
} from '@pop/contracts';

export interface RoomConnection {
  /** Dernier état complet reçu ; null tant que le premier State n'est pas là. */
  readonly state: RoomState | null;
  /** Socket actuellement connecté (faux pendant une reconnexion). */
  readonly connected: boolean;
  /** Dernière annonce de manche reçue (le State reste la vérité). */
  readonly roundAnnouncement: Round | null;
  /** Verdict de la dernière manche résolue. */
  readonly lastResolution: RoundResolved | null;
  /** Classement final quand la partie est terminée. */
  readonly finished: Finished | null;
  /** Dernier rejet d'action, à afficher puis à ignorer. */
  readonly rejection: Rejected | null;
  /** L'hôte lance la partie. */
  readonly start: () => void;
  /** Le joueur actif soumet sa réponse. */
  readonly answer: (text: string) => void;
  /** Redemande explicitement l'état complet. */
  readonly sync: () => void;
  /** Fait disparaître le dernier message de rejet. */
  readonly dismissRejection: () => void;
}

export function useRoom(code: string, playerId: string): RoomConnection {
  const [state, setState] = useState<RoomState | null>(null);
  const [connected, setConnected] = useState(false);
  const [roundAnnouncement, setRoundAnnouncement] = useState<Round | null>(null);
  const [lastResolution, setLastResolution] = useState<RoundResolved | null>(null);
  const [finished, setFinished] = useState<Finished | null>(null);
  const [rejection, setRejection] = useState<Rejected | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const socket: Socket = io({ auth: { code, playerId } });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit(CLIENT_EVENT.Sync);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on(SERVER_EVENT.State, (payload: unknown) => {
      const parsed = roomStateSchema.safeParse(payload);
      if (parsed.success) {
        setState(parsed.data);
      }
    });

    socket.on(SERVER_EVENT.RoundStarted, (payload: unknown) => {
      const parsed = roundSchema.safeParse(payload);
      if (parsed.success) {
        setRoundAnnouncement(parsed.data);
      }
    });

    socket.on(SERVER_EVENT.RoundResolved, (payload: unknown) => {
      const parsed = roundResolvedSchema.safeParse(payload);
      if (parsed.success) {
        setLastResolution(parsed.data);
      }
    });

    socket.on(SERVER_EVENT.Finished, (payload: unknown) => {
      const parsed = finishedSchema.safeParse(payload);
      if (parsed.success) {
        setFinished(parsed.data);
      }
    });

    socket.on(SERVER_EVENT.Rejected, (payload: unknown) => {
      const parsed = rejectedSchema.safeParse(payload);
      if (parsed.success) {
        setRejection(parsed.data);
      }
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [code, playerId]);

  const start = useCallback(() => {
    socketRef.current?.emit(CLIENT_EVENT.Start);
  }, []);

  const answer = useCallback((text: string) => {
    socketRef.current?.emit(CLIENT_EVENT.Answer, { text });
  }, []);

  const sync = useCallback(() => {
    socketRef.current?.emit(CLIENT_EVENT.Sync);
  }, []);

  const dismissRejection = useCallback(() => {
    setRejection(null);
  }, []);

  return {
    state,
    connected,
    roundAnnouncement,
    lastResolution,
    finished,
    rejection,
    start,
    answer,
    sync,
    dismissRejection,
  };
}
